import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

export const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <Button
          onClick={() => navigate('/')}
          variant="ghost"
          className="mb-8 text-gray-400 hover:text-white"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Torna alla Home
        </Button>

        <h1 className="text-4xl font-bold mb-8">Privacy Policy</h1>
        <p className="text-gray-400 mb-8">Ultimo aggiornamento: {new Date().toLocaleDateString('it-IT')}</p>

        <div className="space-y-8 text-gray-300 leading-relaxed">
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">1. Introduzione</h2>
            <p>
              Digital Performances (di seguito "noi", "nostro") rispetta la privacy dei visitatori del proprio sito web
              e si impegna a proteggere i dati personali degli utenti in conformità con il Regolamento (UE) 2016/679
              (GDPR) e il D.Lgs. 196/2003 (Codice Privacy italiano).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">2. Titolare del Trattamento</h2>
            <p className="mb-2"><strong>Digital Performances</strong></p>
            <p>Email: info@digitalperformances.it</p>
            <p>Email assistenza: assistenza@digitalperformances.it</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">3. Dati Raccolti</h2>
            <p className="mb-4">Raccogliamo e trattiamo le seguenti categorie di dati personali:</p>
            
            <h3 className="text-xl font-semibold text-white mb-3">3.1 Dati di Navigazione</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Indirizzo IP (anonimizzato tramite Google Analytics)</li>
              <li>Tipo di browser</li>
              <li>Sistema operativo</li>
              <li>Pagine visitate e durata della visita</li>
              <li>Data e ora di accesso</li>
              <li>Sorgente di traffico (da dove proviene il visitatore)</li>
              <li>Interazioni con il sito (click, scroll, tempo di permanenza)</li>
            </ul>
            
            <div className="bg-zinc-900 p-4 rounded-none border-l-4 border-white mb-4">
              <p className="text-sm">
                <strong>Nota su Google Analytics:</strong> Utilizziamo Google Analytics per analizzare 
                l'utilizzo del sito. Google Analytics raccoglie informazioni anonime o pseudonime tramite cookie. 
                L'indirizzo IP viene anonimizzato prima della memorizzazione. Puoi disattivare Google Analytics 
                installando il componente aggiuntivo del browser disponibile su{' '}
                <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-white underline">
                  https://tools.google.com/dlpage/gaoptout
                </a>
              </p>
            </div>

            <h3 className="text-xl font-semibold text-white mb-3">3.2 Dati Forniti Volontariamente</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Nome e cognome (quando fornito via email o chatbot)</li>
              <li>Indirizzo email</li>
              <li>Informazioni fornite tramite il chatbot Voiceflow</li>
              <li>Contenuto delle comunicazioni inviate</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4. Finalità del Trattamento</h2>
            <p className="mb-4">I dati personali sono trattati per le seguenti finalità:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Fornitura del servizio:</strong> Gestione delle richieste, assistenza clienti</li>
              <li><strong>Comunicazioni:</strong> Risposta a domande e richieste di informazioni</li>
              <li><strong>Miglioramento del servizio:</strong> Analisi dell'utilizzo del sito per migliorare l'esperienza utente</li>
              <li><strong>Adempimenti legali:</strong> Obblighi previsti dalla legge</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5. Base Giuridica del Trattamento</h2>
            <p className="mb-4">Il trattamento dei dati è basato su:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Consenso:</strong> Per l'invio di comunicazioni commerciali</li>
              <li><strong>Legittimo interesse:</strong> Per l'analisi statistica e il miglioramento del servizio</li>
              <li><strong>Esecuzione contrattuale:</strong> Per fornire i servizi richiesti</li>
              <li><strong>Obblighi di legge:</strong> Per adempimenti fiscali e contabili</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. Conservazione dei Dati</h2>
            <p>
              I dati personali saranno conservati per il tempo necessario a soddisfare le finalità per cui sono stati
              raccolti e comunque non oltre:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Dati di navigazione: 12 mesi</li>
              <li>Dati di contatto: fino a revoca del consenso o richiesta di cancellazione</li>
              <li>Dati contrattuali: 10 anni (obblighi fiscali e contabili)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7. Condivisione dei Dati</h2>
            <p className="mb-4">I dati personali possono essere condivisi con:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Google Analytics:</strong> Per l'analisi statistica del traffico web. 
                Raccoglie dati anonimi o pseudonimi come indirizzo IP (anonimizzato), pagine visitate, 
                tipo di dispositivo e browser. Google può utilizzare questi dati secondo la propria 
                Privacy Policy (
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-white underline">
                  https://policies.google.com/privacy
                </a>)
              </li>
              <li><strong>Voiceflow:</strong> Per il funzionamento del chatbot (Privacy Policy: https://www.voiceflow.com/privacy)</li>
              <li><strong>Provider hosting:</strong> Per l'erogazione del servizio web</li>
              <li><strong>Fornitori di servizi:</strong> Professionisti e consulenti (sotto vincolo di riservatezza)</li>
            </ul>
            <p className="mt-4">
              Non vendiamo, affittiamo o scambiamo i dati personali con terze parti per scopi di marketing.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8. Diritti dell'Interessato</h2>
            <p className="mb-4">In conformità al GDPR, hai diritto a:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Accesso:</strong> Ottenere conferma dell'esistenza dei tuoi dati e riceverne copia</li>
              <li><strong>Rettifica:</strong> Correggere dati inesatti o incompleti</li>
              <li><strong>Cancellazione:</strong> Richiedere la cancellazione dei dati ("diritto all'oblio")</li>
              <li><strong>Limitazione:</strong> Limitare il trattamento in determinati casi</li>
              <li><strong>Portabilità:</strong> Ricevere i dati in formato strutturato</li>
              <li><strong>Opposizione:</strong> Opporti al trattamento per motivi legittimi</li>
              <li><strong>Revoca consenso:</strong> Revocare il consenso in qualsiasi momento</li>
            </ul>
            <p className="mt-4">
              Per esercitare questi diritti, contattaci a: <a href="mailto:info@digitalperformances.it" className="text-white underline">info@digitalperformances.it</a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">9. Cookie</h2>
            <p>
              Il sito utilizza cookie tecnici e di terze parti. Per maggiori informazioni, consulta la nostra{' '}
              <a href="/cookie-policy" className="text-white underline">Cookie Policy</a>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">10. Sicurezza dei Dati</h2>
            <p>
              Adottiamo misure tecniche e organizzative adeguate per proteggere i dati personali da accessi non
              autorizzati, perdita, distruzione o divulgazione non autorizzata, tra cui:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Crittografia SSL/TLS per la trasmissione dei dati</li>
              <li>Accesso limitato ai dati solo al personale autorizzato</li>
              <li>Backup regolari dei sistemi</li>
              <li>Monitoraggio continuo delle vulnerabilità</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">11. Trasferimento Dati Extra-UE</h2>
            <p>
              Alcuni fornitori di servizi potrebbero avere server ubicati al di fuori dell'Unione Europea. In tal caso,
              garantiamo che il trasferimento avvenga in conformità con le clausole contrattuali standard approvate
              dalla Commissione Europea o altri meccanismi di salvaguardia adeguati.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">12. Minori</h2>
            <p>
              I nostri servizi non sono destinati a minori di 18 anni. Non raccogliamo consapevolmente dati personali
              di minori. Se veniamo a conoscenza di aver raccolto dati di un minore, procederemo immediatamente alla
              loro cancellazione.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">13. Modifiche alla Privacy Policy</h2>
            <p>
              Ci riserviamo il diritto di modificare questa Privacy Policy in qualsiasi momento. Le modifiche saranno
              pubblicate su questa pagina con indicazione della data di ultimo aggiornamento. Ti invitiamo a consultare
              regolarmente questa pagina.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">14. Reclami</h2>
            <p>
              Hai il diritto di proporre reclamo all'Autorità Garante per la Protezione dei Dati Personali se ritieni
              che il trattamento dei tuoi dati violi il GDPR:
            </p>
            <p className="mt-4">
              <strong>Garante per la protezione dei dati personali</strong><br />
              Piazza Venezia, 11 - 00187 Roma<br />
              Tel: (+39) 06.696771<br />
              Fax: (+39) 06.69677.3785<br />
              Email: garante@gpdp.it<br />
              PEC: protocollo@pec.gpdp.it<br />
              Web: www.garanteprivacy.it
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">15. Contatti</h2>
            <p>
              Per qualsiasi domanda o richiesta relativa alla presente Privacy Policy o al trattamento dei dati
              personali, puoi contattarci a:
            </p>
            <p className="mt-4">
              <strong>Digital Performances</strong><br />
              Email: <a href="mailto:info@digitalperformances.it" className="text-white underline">info@digitalperformances.it</a><br />
              Email assistenza: <a href="mailto:assistenza@digitalperformances.it" className="text-white underline">assistenza@digitalperformances.it</a><br />
              WhatsApp: <a href="https://wa.me/393793460226" target="_blank" rel="noopener noreferrer" className="text-white underline">+39 379 346 0226</a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};